import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Codes() {
  const [codes, setCodes] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/codes").then((r) => setCodes(r.data));
  useEffect(() => {
    load();
    api.get("/affiliates").then((r) => setAffiliates(r.data));
  }, []);

  const affName = (id) => affiliates.find((a) => a.id === id)?.name || "—";

  return (
    <div>
      <PageHeader
        title="Discount Codes"
        subtitle={`${codes.length} codes assigned · Discounts configured in Shopify`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-10" data-testid="new-code-btn"><Plus className="h-4 w-4 mr-1.5" /> New Code</Button>
            </DialogTrigger>
            <NewCodeDialog affiliates={affiliates} onCreated={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="p-6 md:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {codes.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border/60 bg-card p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xl font-mono font-bold">{c.code}</div>
                <button
                  onClick={() => { navigator.clipboard.writeText(c.referral_link || c.code); toast.success("Copied"); }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`copy-${c.id}`}
                ><Copy className="h-4 w-4" /></button>
              </div>
              <div className="text-sm text-muted-foreground mb-3">{affName(c.affiliate_id)}</div>
              <div className="flex items-center justify-between text-xs">
                <div><span className="font-semibold">{c.usage_count}</span> uses</div>
                {c.referral_link && (
                  <a
                    href={c.referral_link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-primary truncate max-w-[60%] underline-offset-2 hover:underline"
                  >{c.referral_link.replace(/^https?:\/\//, "")}</a>
                )}
              </div>
            </div>
          ))}
          {codes.length === 0 && <div className="col-span-3 text-sm text-muted-foreground p-8">No codes yet.</div>}
        </div>
      </div>
    </div>
  );
}

function NewCodeDialog({ affiliates, onCreated }) {
  const [form, setForm] = useState({ code: "", affiliate_id: affiliates[0]?.id || "" });
  const submit = async () => {
    try {
      const link = `https://fuelled.in/?ref=${form.code.toUpperCase()}`;
      await api.post("/codes", { ...form, code: form.code.toUpperCase(), referral_link: link });
      toast.success("Code created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <DialogContent className="rounded-3xl">
      <DialogHeader><DialogTitle>New Discount Code</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Code</Label><Input data-testid="new-code-input" value={form.code} onChange={set("code")} placeholder="e.g. PRIYA25" className="mt-1 rounded-xl uppercase font-mono" /></div>
        <div><Label>Affiliate</Label>
          <select data-testid="new-code-aff" value={form.affiliate_id} onChange={set("affiliate_id")} className="mt-1 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm">
            {affiliates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="text-[11px] text-muted-foreground">Discount amount is configured in Shopify. Referral link auto-generated as <code className="font-mono">https://fuelled.in/?ref={form.code.toUpperCase() || "CODE"}</code></div>
      </div>
      <DialogFooter>
        <Button data-testid="submit-code" onClick={submit} className="rounded-full">Create</Button>
      </DialogFooter>
    </DialogContent>
  );
}
